import { useEffect, useRef } from 'react';

import _ from 'lodash';

/**
 * Debouncing wrapper for the functions used in the React context
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid lot of onChange events in the non-simple text editor
 * const Editor = ({ onChange }) => {
 *     const debouncedOnChange = useDebounce(onChange, 500);
 *
 *     return <NonSimpleEditor onChange={debouncedOnChange} />
 * }
 *
 * @returns {Function}
 */
const useDebounce = (func, wait, options) => {
  const ref = useRef(null);

  if (ref.current === null) {
    ref.current = _.debounce(func, wait, options);
  }

  useEffect(
    () => () => {
      if (ref.current === null) {
        return;
      }

      // Cancel debounced call on component unmount
      ref.current.cancel();
    },
    [],
  );

  return ref.current;
};

export default useDebounce;
